import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Target, Download, Loader, AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import { buildApiUrl } from '@/config/appConfig';
import PriorityUseCaseCard from '../components/PriorityUseCaseCard';

const ActionBoardPriority = () => {
  const { clientId } = useParams();
  const currentClientId = clientId || '1';

  const [useCases, setUseCases] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllUseCases, setShowAllUseCases] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [currentClientId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch use cases with scores
      const useCasesRes = await fetch(
        buildApiUrl(`/api/v2/useCases/list?clientId=${currentClientId}`)
      );
      if (!useCasesRes.ok) throw new Error('Failed to fetch use cases');
      const useCasesData = await useCasesRes.json();

      // Fetch actions
      const actionsRes = await fetch(
        buildApiUrl(`/api/v2/actions/${currentClientId}`)
      );
      if (!actionsRes.ok) throw new Error('Failed to fetch actions');
      const actionsData = await actionsRes.json();

      setUseCases(useCasesData.data || []);
      setActions(actionsData.actions || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update action status
  const handleActionUpdate = async (actionId, newStatus) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v2/actions/${actionId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: String(currentClientId), // 🔧 FIXED: Changed from parseInt to String
          status: newStatus
        })
      });

      if (!response.ok) throw new Error('Failed to update action');

      // Update local state
      setActions(prev => prev.map(action =>
        action.action_id === actionId
          ? { ...action, status: newStatus, updated_at: new Date().toISOString() }
          : action
      ));
    } catch (err) {
      console.error('Error updating action:', err);
      alert('Failed to update action status');
    }
  };

  // Export CSV
  const handleExport = () => {
    const displayedUseCases = showAllUseCases ? useCases : useCases.slice(0, 10);
    const useCaseIds = new Set(displayedUseCases.map(uc => uc.use_case_id));
    const filteredActions = actions.filter(a => useCaseIds.has(a.use_case_id));

    const csvRows = [
      ['Use Case', 'Rank', 'Action', 'Description', 'Role', 'Due Window', 'Priority', 'Status', 'Dependencies']
    ];

    filteredActions.forEach(action => {
      const useCase = useCases.find(uc => uc.use_case_id === action.use_case_id);
      csvRows.push([
        useCase?.name || 'Unknown',
        useCase?.rank || '',
        action.label,
        action.description || '',
        action.role,
        action.due_window,
        action.priority,
        action.status,
        (action.dependencies || []).join('; ')
      ]);
    });

    const csvContent = csvRows.map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `action-board-priority-client-${currentClientId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calculate stats
  const displayedUseCases = showAllUseCases ? useCases : useCases.slice(0, 10);
  const displayedUseCaseIds = new Set(displayedUseCases.map(uc => uc.use_case_id));
  const displayedActions = actions.filter(a => displayedUseCaseIds.has(a.use_case_id));

  const stats = {
    totalActions: displayedActions.length,
    openActions: displayedActions.filter(a => a.status === 'open').length,
    inProgressActions: displayedActions.filter(a => a.status === 'in-progress').length,
    completedActions: displayedActions.filter(a => a.status === 'completed').length,
    highPriorityActions: displayedActions.filter(a => a.priority === 'High').length
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your priority actions...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Action Board</h1>
                <p className="text-sm text-gray-600">Priority-driven implementation roadmap</p>
              </div>
            </div>

            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.totalActions}</div>
            <div className="text-sm text-gray-600">Total Actions</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.openActions}</div>
            <div className="text-sm text-gray-600">Open</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.inProgressActions}</div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{stats.completedActions}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-red-600">{stats.highPriorityActions}</div>
            <div className="text-sm text-gray-600">High Priority</div>
          </div>
        </div>

        {/* Toggle Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {showAllUseCases ? 'All Use Cases' : 'Top 10 Priority Use Cases'}
          </h2>
          
          <button
            onClick={() => setShowAllUseCases(!showAllUseCases)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {showAllUseCases ? (
              <>
                <ToggleRight className="h-5 w-5 text-blue-600" />
                <span>Showing All {useCases.length} Use Cases</span>
              </>
            ) : (
              <>
                <ToggleLeft className="h-5 w-5 text-gray-400" />
                <span>Show All Use Cases</span>
              </>
            )}
          </button>
        </div>

        {/* Use Case Cards */}
        <div className="space-y-4">
          {displayedUseCases.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No use cases found for this client</p>
            </div>
          ) : (
            displayedUseCases.map(useCase => (
              <PriorityUseCaseCard
                key={useCase.use_case_id}
                useCase={useCase}
                actions={actions}
                onActionUpdate={handleActionUpdate}
              />
            ))
          )}
        </div>

        {/* Footer Info */}
        {!showAllUseCases && useCases.length > 10 && (
          <div className="mt-6 text-center text-sm text-gray-600">
            Showing top 10 of {useCases.length} use cases. 
            <button
              onClick={() => setShowAllUseCases(true)}
              className="ml-2 text-blue-600 hover:underline font-medium"
            >
              Show all {useCases.length} use cases
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionBoardPriority;